"use client";

import type { ReactNode } from "react";
import { LanguageToggle, ThemeToggle } from "@/components/common/toggles";
import { PageTitleProvider, usePageTitle } from "@/context/page-title";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui";
import AuthButton from "@/components/common/auth/Button";
import { DemoOrganizationChrome } from "@/components/common/demo";
import { useActiveOrganization } from "@/context/active-organization";
import { useTranslate } from "@tolgee/react";
import { Menu } from "./Menu";
import { SIDEBAR_SHELL_PROVIDER_CLASSES } from "./styles";

export type SidebarShellLayoutProps = {
  children: ReactNode;
  mobileActions: ReactNode;
  banner?: ReactNode;
};

const SidebarShell = ({ children, mobileActions, banner }: SidebarShellLayoutProps) => {
  const { title } = usePageTitle();
  const { organizations } = useActiveOrganization();
  const noOrganization = organizations.length === 0;
  const { t } = useTranslate();

  return (
    <>
      {!noOrganization && <Menu />}
      {/* z-0 stacking context keeps Leaflet/MapLibre panes (z-index 400–1000) under the sidebar */}
      <SidebarInset className="relative z-0 min-w-0">
        <div className="flex w-full items-center gap-2 px-4 pt-2 md:px-6 lg:px-8">
          {!noOrganization && (
            <SidebarTrigger className="md:hidden" aria-label={t("nav.opennavigation")} />
          )}
          <h1 className="min-w-0 flex-1 truncate text-2xl font-bold">{title}</h1>
          <div className="flex items-center gap-2 md:hidden">{mobileActions}</div>
          <div className="hidden items-center gap-2 md:flex">
            <LanguageToggle triggerAs="sidebar-header" />
            <ThemeToggle triggerAs="sidebar-header" />
            <AuthButton triggerAs="sidebar" />
          </div>
        </div>
        {banner}
        <div className="relative grow overflow-hidden">
          <div className="absolute inset-0 overflow-auto">{children}</div>
        </div>
      </SidebarInset>
    </>
  );
};

export const SidebarShellLayout = ({
  children,
  mobileActions,
  banner,
}: SidebarShellLayoutProps) => (
  <PageTitleProvider>
    <DemoOrganizationChrome>
      <SidebarProvider className={SIDEBAR_SHELL_PROVIDER_CLASSES}>
        <SidebarShell mobileActions={mobileActions} banner={banner}>
          {children}
        </SidebarShell>
      </SidebarProvider>
    </DemoOrganizationChrome>
  </PageTitleProvider>
);
